import {
  collection,
  getDocs,
  writeBatch,
  doc,
  serverTimestamp,
  Firestore,
} from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { useHeatmapStore } from "./heatmapStore";
import { getAuth } from "firebase/auth";

export interface LgsDoc {
  id?: string;
  name: string;
  address?: string;
  lat: number;
  lng: number;
  source: string;          // "firestore" | "wpn"
  wpnId?: string;
  updatedAt?: any;
}

function norm(str: string) {
  return str
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[^\w\d ]/g, "")
    .trim();
}

function buildKey(obj: Partial<LgsDoc>) {
  if (obj.wpnId) return `wpn:${obj.wpnId}`;
  return `na:${norm(obj.name || "")}|${norm(obj.address || "")}`;
}

async function fetchWpn(): Promise<LgsDoc[]> {
  // Try local emulator first, then prod
  const local = `${window.location.protocol}//localhost:5001/spellgather-beta/us-central1/fetchWpnStores`;
  const prod = import.meta.env.VITE_WPN_FUNCTION_URL; // set in .env
  const urls = [local, prod].filter(Boolean);
  for (const u of urls) {
    try {
      const r = await fetch(u!, { method: "POST" });
      if (!r.ok) continue;
      const json = await r.json();
      if (Array.isArray(json)) {
        return json
          .map((s: any) => ({
            name: s.name,
            address: s.address,
            lat: s.lat ?? s.latitude,
            lng: s.lng ?? s.longitude,
            wpnId: s.id || s.wpnId,
            source: "wpn",
          }))
          .filter((x: LgsDoc) => typeof x.lat === "number" && typeof x.lng === "number" && x.name);
      }
    } catch {
      /* try next */
    }
  }
  return [];
}

export async function syncLgsOnce(force = false) {
  const state = useHeatmapStore.getState();
  if (state._lgsSyncInFlight && !force) return;
  state._setLgsSyncInFlight(true);

  try {
    const db = getFirestore();
    const existingSnap = await getDocs(collection(db, "lgs"));
    const existing: LgsDoc[] = [];
    const keyMap = new Map<string, LgsDoc>();

    existingSnap.forEach((d) => {
      const data = d.data() as any;
      const docObj: LgsDoc = {
        id: d.id,
        name: data.name,
        address: data.address,
        lat: data.lat ?? data.latitude,
        lng: data.lng ?? data.longitude,
        source: data.source || "firestore",
        wpnId: data.wpnId,
        updatedAt: data.updatedAt,
      };
      const k = buildKey(docObj);
      keyMap.set(k, docObj);
      existing.push(docObj);
    });

    // Immediately push existing to UI if not present
    if (!state.lgsPoints.length) {
      state._setLgsPoints(existing);
    }

    const wpnList = await fetchWpn();
    if (wpnList.length) {
      const toInsert: LgsDoc[] = [];
      for (const w of wpnList) {
        const k = buildKey(w);
        if (!keyMap.has(k)) {
          keyMap.set(k, w);
          toInsert.push(w);
        }
      }
      if (toInsert.length) {
        const auth = getAuth();
        const uid = auth.currentUser?.uid;
        const batch = writeBatch(db);
        toInsert.forEach((w) => {
          const ref = doc(collection(db, "lgs"));
          batch.set(ref, {
            name: w.name,
            address: w.address || "",
            lat: w.lat,
            lng: w.lng,
            wpnId: w.wpnId || null,
            source: "wpn",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: uid, // must match auth user per rules
          });
        });
        try { await batch.commit(); } catch {}
      }
    }

    // Final merged list
    const merged = Array.from(keyMap.values());
    state._setLgsPoints(merged);
    state._setLgsError(null);
  } catch (e: any) {
    useHeatmapStore.getState()._setLgsError(
      e?.message || "Failed to sync LGS"
    );
  } finally {
    useHeatmapStore.getState()._setLgsSyncInFlight(false);
  }
}
